/**
 * @fileoverview Service Bus Explorer API Service
 * @description API calls for universal Service Bus dead-letter queue search
 */

import { apiClient } from '@/services';

const BASE = '/v1/servicebus-explorer';

export const serviceBusExplorerApi = {
  health: () => apiClient.get(`${BASE}/health`),

  /**
   * Search Service Bus queue or topic subscription with dynamic key-value criteria
   * @param {string} connectionString - Azure Service Bus connection string
   * @param {Array<{key: string, value: string}>} criteria - Search criteria
   * @param {string} [queueName] - Optional queue/topic name override
   * @param {string} [queueTarget='deadletter'] - 'deadletter', 'active', or 'both'
   * @param {string} [subscriptionName] - Set to read a topic subscription
   * @returns {Promise<{total_scanned, match_count, matches[], error}>}
   */
  search: (connectionString, criteria, queueName = '', queueTarget = 'deadletter', subscriptionName = '') =>
    apiClient.post(`${BASE}/search`, {
      connection_string: connectionString,
      queue_name: queueName,
      subscription_name: subscriptionName,
      queue_target: queueTarget,
      criteria,
    }),

  /**
   * Peek a sample message and return its discoverable JSON field paths
   * @param {string} connectionString - Azure Service Bus connection string
   * @param {string} [queueName] - Optional queue/topic name override
   * @param {string} [queueTarget='deadletter'] - 'deadletter', 'active', or 'both'
   * @param {string} [subscriptionName] - Set to read a topic subscription
   * @returns {Promise<{fields: Array<{path, sample}>, sample_message, scanned, error}>}
   */
  sampleFields: (connectionString, queueName = '', queueTarget = 'deadletter', subscriptionName = '') =>
    apiClient.post(`${BASE}/sample-fields`, {
      connection_string: connectionString,
      queue_name: queueName,
      subscription_name: subscriptionName,
      queue_target: queueTarget,
    }),

  /** List queues and topics in the namespace (requires Manage rights). */
  listEntities: (connectionString) =>
    apiClient.post(`${BASE}/entities`, { connection_string: connectionString }),

  /** List subscriptions for a topic (requires Manage rights). */
  listSubscriptions: (connectionString, topicName = '') =>
    apiClient.post(`${BASE}/subscriptions`, {
      connection_string: connectionString,
      topic_name: topicName,
    }),

  /** List the current user's saved connections (metadata only, no secrets). */
  listConnections: () => apiClient.get(`${BASE}/connections`),

  /** Save (or update) a named connection string for the current user. */
  saveConnection: (name, connectionString, meta = {}) =>
    apiClient.post(`${BASE}/connections`, {
      name,
      connection_string: connectionString,
      kind: meta.kind || '',
      entity_name: meta.entityName || '',
      subscription_name: meta.subscriptionName || '',
    }),

  /** Reveal the decrypted connection string for one saved connection (owner only). */
  revealConnection: (id) => apiClient.get(`${BASE}/connections/${id}`),

  /** Delete one of the current user's saved connections. */
  deleteConnection: (id) => apiClient.delete(`${BASE}/connections/${id}`),
};
